import {
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Link,
  Select,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { handleHookFormHTTPError } from "src/common/form";
import { useKy } from "src/common/ky";
import { Loading } from "src/common/Loading";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderBackLink,
  PageHeaderSubtitle,
  PageHeaderTitle,
} from "src/layout/PageHeader";
import { INTERCOMS_URL } from "src/routes/ProductUnits/Intercoms";
import {
  GET_ALL_INTERCOM_MODELS_QUERY_KEY,
  useGetAllIntercomModelsQuery,
} from "src/routes/ProductUnits/Intercoms/queries";

interface IntercomValues {
  intercomModelId: string;
  serialNumber: string;
  notes: string;
  entryPointId: string;
}

const IntercomValidationSchema = Yup.object().shape({
  intercomModelId: Yup.string().required().label("Intercom Model"),
  serialNumber: Yup.string().required().label("Serial Number"),
  notes: Yup.string().label("Notes"),
  entryPointId: Yup.string().required().label("Entry Point ID"),
});

export const IntercomAdd = () => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
    getValues,
  } = useForm<IntercomValues>({
    defaultValues: {
      intercomModelId: "",
      serialNumber: "",
      notes: "",
      entryPointId: "",
    },
    resolver: yupResolver(IntercomValidationSchema),
  });

  const ky = useKy();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const addIntercom = useMutation(
    (values: IntercomValues) => ky.post("intercoms", { json: values }),
    {
      onError: handleHookFormHTTPError(setError, getValues, toast),
      onSuccess: async () => {
        toast({
          description: "Intercom successfully added",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        await queryClient.invalidateQueries(GET_ALL_INTERCOM_MODELS_QUERY_KEY);
        navigate(INTERCOMS_URL);
      },
    }
  );

  const intercomModelsQuery = useGetAllIntercomModelsQuery();

  if (intercomModelsQuery.isLoading || !intercomModelsQuery.data) {
    return <Loading />;
  }

  return (
    <>
      <PageHeader>
        <PageHeaderBackLink>
          <Link as={RouterLink} to={"/product-units"}>
            &#8249; Back to list
          </Link>
        </PageHeaderBackLink>
        <PageHeaderTitle>Add Product Unit to Inventory</PageHeaderTitle>
        <PageHeaderSubtitle>
          Enter intercom unit details here
        </PageHeaderSubtitle>
      </PageHeader>
      <PageContent>
        <form
          onSubmit={handleSubmit((data: IntercomValues) =>
            addIntercom.mutate(data)
          )}
        >
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem colSpan={1}>
              <VStack spacing={4}>
                <Controller
                  name="intercomModelId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isRequired
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="intercomModelId">
                        Product Model
                      </FormLabel>
                      <Select {...field} id="intercomModelId">
                        <option value="" />
                        {intercomModelsQuery.data?.map((intercomModel) => (
                          <option
                            value={intercomModel.intercomModelId}
                            key={intercomModel.intercomModelId}
                          >
                            {intercomModel.name}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  name="serialNumber"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isRequired
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="serialNumber">
                        Serial Number
                      </FormLabel>
                      <Input
                        {...field}
                        onChange={(event) =>
                          field.onChange(event.target.value.toUpperCase())
                        }
                        id="serialNumber"
                      />
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  name="entryPointId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isRequired
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="entryPointId">
                        Entry Point ID
                      </FormLabel>
                      <Input {...field} id="entryPointId" />
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
                <Controller
                  name="notes"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      isInvalid={fieldState.invalid && fieldState.isTouched}
                    >
                      <FormLabel htmlFor="notes">Notes</FormLabel>
                      <Textarea {...field} id="notes" />
                      <FormErrorMessage>
                        {fieldState.error?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                />
              </VStack>
            </GridItem>

            <GridItem colSpan={2}>
              <Divider />
            </GridItem>

            <GridItem colSpan={2}>
              <ButtonGroup display="flex" justifyContent="flex-end">
                <Link as={RouterLink} to={INTERCOMS_URL}>
                  <Button>Cancel</Button>
                </Link>
                <Button
                  type="submit"
                  colorScheme="brand.blue"
                  isLoading={isSubmitting}
                >
                  Add Product Unit
                </Button>
              </ButtonGroup>
            </GridItem>
          </Grid>
        </form>
      </PageContent>
    </>
  );
};
